import { memo, useEffect, useMemo } from 'react'
import clsx from 'clsx'
import { useLocation } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import Logo from '../Logo'
import { usePrefersReducedMotion } from '../../../hooks/usePrefersReducedMotion'
import { GET_PENDING_APPROVALS } from './queries'
import SidebarProfile from './SidebarProfile'
import Navigation from './Navigation'
import { NAVIGATION_ITEMS } from './config'
import type { SidebarProps } from './types'

const deriveEnvironmentLabel = () => {
    const explicitEnv = (import.meta.env.VITE_APP_ENV as string | undefined)?.toLowerCase() ?? ''
    const modeFallback = import.meta.env.MODE?.toLowerCase() ?? ''
    const value = explicitEnv || modeFallback

    if (value.includes('prod')) {
        return 'PROD'
    }

    if (value.includes('staging') || value.includes('stage') || value.includes('pre')) {
        return 'STAGE'
    }

    if (value.includes('qa')) {
        return 'QA'
    }

    return 'DEV'
}

interface PendingApprovalsData {
    getAllUsers: Array<{ id: string; onboarding?: boolean | null }>
}

interface PendingApprovalsVariables {
    limit: number
    page: number
}

const Sidebar = memo(
    ({ appName, collapsed, isMobile, isMobileOpen, onToggleCollapse, onToggleMobile, onCloseMobile }: SidebarProps) => {
        const prefersReducedMotion = usePrefersReducedMotion()
        const location = useLocation()
        const environmentLabel = useMemo(() => deriveEnvironmentLabel(), [])

        const { data: pendingData } = useQuery<PendingApprovalsData, PendingApprovalsVariables>(GET_PENDING_APPROVALS, {
            variables: { limit: 100, page: 1 },
            fetchPolicy: 'cache-and-network',
            pollInterval: 15000,
            notifyOnNetworkStatusChange: true,
        })

        const pendingApprovals = useMemo(() => {
            const users = pendingData?.getAllUsers ?? []
            return users.filter((item: { onboarding?: boolean | null }) => Boolean(item?.onboarding)).length
        }, [pendingData?.getAllUsers])

        useEffect(() => {
            if (!isMobile) {
                return
            }

            const handleEscape = (event: KeyboardEvent) => {
                if (event.key === 'Escape') {
                    onCloseMobile?.()
                }
            }

            window.addEventListener('keydown', handleEscape)

            return () => {
                window.removeEventListener('keydown', handleEscape)
            }
        }, [isMobile, onCloseMobile])

        useEffect(() => {
            if (!isMobile) {
                return
            }
            onCloseMobile?.()
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [location.pathname])

        const sidebarClasses = clsx(
            'tw:z-40 tw:flex tw:h-full tw:flex-col tw:bg-slate-950/95 tw:text-slate-100 tw:border-r tw:border-slate-800',
            prefersReducedMotion
                ? 'tw:transition-[width] tw:duration-75'
                : 'tw:transition-[width,transform] tw:duration-300 tw:ease-in-out',
            isMobile
                ? [
                      'tw:fixed tw:inset-y-0 tw:left-0 tw:w-72 tw:max-w-[18rem] tw:shadow-2xl tw:backdrop-blur-sm tw:transform',
                      isMobileOpen ? 'tw:translate-x-0' : 'tw:-translate-x-full',
                  ]
                : collapsed
                  ? 'tw:w-20'
                  : 'tw:w-72',
        )
        const profileCollapsed = collapsed && !isMobile

        return (
            <>
                {isMobile && isMobileOpen && (
                    <button
                        type="button"
                        aria-hidden="true"
                        className="tw:fixed tw:inset-0 tw:z-30 tw:bg-slate-900/60"
                        onClick={() => onCloseMobile?.()}
                    />
                )}
                <aside className={sidebarClasses}>
                    <div className="tw:flex tw:flex-col tw:flex-1 tw:overflow-hidden">
                        <div
                            className={clsx(
                                'tw:flex tw:items-center tw:gap-2 tw:px-4 tw:py-5',
                                collapsed && !isMobile ? 'tw:flex-col tw:gap-3' : 'tw:justify-between',
                            )}
                        >
                            {isMobile && (
                                <button
                                    type="button"
                                    onClick={isMobile ? onToggleMobile : onToggleCollapse}
                                    className="tw:flex tw:h-9 tw:w-9 tw:items-center tw:justify-center tw:rounded-xl tw:border tw:border-slate-800 tw:text-slate-300 tw:transition-colors hover:tw:bg-slate-800 hover:tw:text-white focus-visible:tw:outline-none focus-visible:tw:ring-2 focus-visible:tw:ring-blue-500"
                                    aria-label={
                                        isMobile
                                            ? isMobileOpen
                                                ? 'Close sidebar'
                                                : 'Open sidebar'
                                            : collapsed
                                              ? 'Expand sidebar'
                                              : 'Collapse sidebar'
                                    }
                                    aria-expanded={isMobile ? isMobileOpen : !collapsed}
                                >
                                    <svg
                                        className="tw:h-5 tw:w-5"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M6 6L18 18"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                        />
                                        <path
                                            d="M18 6L6 18"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                        />
                                    </svg>
                                </button>
                            )}
                            {!collapsed && !isMobile && (
                                <div className="tw:flex tw:flex-1 tw:items-center tw:gap-3 tw:truncate">
                                    <Logo />
                                    <span className="tw:text-lg tw:font-semibold tw:text-white tw:truncate">
                                        {appName || 'Turba Superadmin'}
                                    </span>
                                </div>
                            )}
                            {(collapsed || isMobile) && (
                                <div className="tw:flex tw:items-center tw:gap-2 tw:text-white">
                                    <Logo />
                                    {!collapsed && isMobile && (
                                        <span className="tw:text-base tw:font-semibold tw:text-white tw:truncate">
                                            {appName || 'Turba Superadmin'}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                        {!profileCollapsed && (
                            <SidebarProfile collapsed={profileCollapsed} environmentLabel={environmentLabel} />
                        )}
                        <Navigation
                            items={NAVIGATION_ITEMS}
                            collapsed={profileCollapsed}
                            pendingApprovals={pendingApprovals}
                            onNavigate={isMobile ? onCloseMobile : undefined}
                        />
                    </div>
                </aside>
            </>
        )
    },
)

Sidebar.displayName = 'Sidebar'

export default Sidebar
